import { useState } from 'react'

// One-time onboarding nudge for new customers who haven't enabled
// marketing automation yet.
//
// Appears on the Daily Huddle once the customer has enough data for
// the AI drafts to be useful (≥5 menu items). Tapping the primary
// CTA switches to the Marketing tab where they can enable.
// "Not now" dismisses the card permanently for that restaurant on
// that device — we don't want to nag, this is a soft sell.
//
// Dismissal is localStorage-scoped to restaurant_id so a manager
// who runs multiple locations sees the nudge once per restaurant.
// Cross-device dismissal isn't important here — they'll see it on
// a new device, dismiss again, and move on. Adding a database
// column for this would be over-engineering.

const STORAGE_KEY = (restaurantId) => `marketing_nudge_dismissed:${restaurantId}`

export function shouldShowMarketingNudge({
  restaurantId,
  itemCount,
  hasMarketingEnabled,
}) {
  if (!restaurantId) return false
  if (hasMarketingEnabled) return false
  if (itemCount < 5) return false
  try {
    if (localStorage.getItem(STORAGE_KEY(restaurantId))) return false
  } catch { /* localStorage blocked? assume show */ }
  return true
}

export default function MarketingNudgeCard({
  restaurantId,
  onEnable,
  onDismiss,
}) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null

  function handleDismiss() {
    try { localStorage.setItem(STORAGE_KEY(restaurantId), String(Date.now())) }
    catch { /* clipboard might be blocked; UI still works */ }
    setDismissed(true)
    onDismiss?.()
  }

  return (
    <section
      className="mx-4 mt-3 rounded-2xl border border-amber-brand-200 bg-gradient-to-br from-amber-brand-50 to-amber-50 p-4 shadow-sm"
      aria-label="Try AI marketing drafts"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-status-blue-bg px-2 py-0.5 text-[11px] font-medium text-status-blue-text">
              New
            </span>
            <h3 className="text-base font-medium text-amber-brand-900">
              Try AI-drafted social media
            </h3>
          </div>
          <p className="mt-1.5 text-sm leading-snug text-amber-brand-900/85">
            We can write 5 ready-to-post social media drafts for you every
            week, using your real menu items. You approve in one tap, paste
            into Meta Business Suite or TikTok. Free on every plan.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onEnable}
              className="rounded-lg bg-amber-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-brand-800"
            >
              Enable automation →
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-lg border border-amber-brand-200 bg-white/60 px-3 py-2 text-sm font-medium text-amber-brand-900/70 hover:bg-white"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
